import React, { useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import Swal from "sweetalert2";
import { useAuth } from "hooks/useAuth";
import { useMeal } from "contexts/v1/masterData/MealContext";
import { useUpdateData } from "hooks/useApiHooks";
import { printBookingPOS } from "../../utils/BookingPOSPrinter";

const PrintTicket = ({ initialData, onSuccess, onError }) => {
  const apiUrl = `/reservation/${initialData.id}/meal`;
  const { meals } = useMeal();
  const { user } = useAuth();
  const { updateData: updateMeal } = useUpdateData(apiUrl);
  const [includedItems, setIncludedItems] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [printQueue, setPrintQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);

  useEffect(() => {
    if (!meals?.data || !initialData?.reservation_meal) return;
    const mealData = meals.data.map((meal) => {
      const reservationMeal = initialData.reservation_meal.find((item) => item.meal_id === meal.id);
      return {
        meal_id: meal.id,
        name: meal.name,
        qty: reservationMeal ? reservationMeal.quantity : 0,
        note: reservationMeal ? reservationMeal.note : "",
      };
    });
    setIncludedItems(mealData);
  }, [initialData, meals]);

  const allActivities = initialData.activities?.filter((activity) => activity.type === "product" || activity.type === "extra") || [];

  const handleMealChange = (index, key, value) => {
    setIncludedItems((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item));
  };

  const handleSubmit = async () => {
    const mealData = includedItems.filter((item) => Number(item.qty) > 0).map((item) => ({
      meal_id: item.meal_id,
      quantity: item.qty,
      note: item.note,
    }));

    await updateMeal({ meals: mealData }, apiUrl);
    Swal.fire("Success", "Meals updated successfully", "success");
    return true;
  };

  const getPrintQueue = (printTickets) => {
    if (printTickets.activity) {
      return ["GUIDE", "RESTO", "CUSTOMER"].map((label) => ({
        label,
        printTickets,
      }));
    }

    if (printTickets.meal) {
      return ["RESTO", "CUSTOMER"].map((label) => ({
        label,
        printTickets,
      }));
    }

    return [];
  };

  const finishQueue = () => {
    setPrintQueue([]);
    setQueueIndex(0);
    setIsProcessing(false);
    onSuccess?.();
  };

  const handlePrintCurrent = async () => {
    const current = printQueue[queueIndex];
    if (!current) {
      finishQueue();
      return;
    }

    setIsProcessing(true);
    try {
      await printBookingPOS(initialData, {
        type: "ticket",
        printTickets: current.printTickets,
        copyLabels: [current.label],
        user,
        activities: allActivities,
        meals: includedItems,
      });

      if (queueIndex + 1 >= printQueue.length) {
        finishQueue();
      } else {
        setQueueIndex((currentIndex) => currentIndex + 1);
        setIsProcessing(false);
      }
    } catch (error) {
      console.error("Gagal mencetak tiket!", error);
      Swal.fire("Error", "Failed to print.", "error");
      setIsProcessing(false);
      onError?.();
    }
  };

  const handleSkipCurrent = () => {
    if (queueIndex + 1 >= printQueue.length) {
      finishQueue();
      return;
    }

    setQueueIndex((currentIndex) => currentIndex + 1);
  };

  const handleSaveAndPrint = async (printTickets) => {
    setIsProcessing(true);
    try {
      const saved = await handleSubmit();
      if (!saved) return;

      const queue = getPrintQueue(printTickets);
      if (!queue.length) {
        finishQueue();
        return;
      }

      setPrintQueue(queue);
      setQueueIndex(0);
    } catch (error) {
      console.error("Gagal menyimpan dan mencetak!", error);
      Swal.fire("Error", "Failed to save and print.", "error");
      onError?.();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form className="d-flex flex-column" style={{ height: "100%" }}>
      <div className="action-sheet-content flex-grow-1">
        <div className="form-group mb-3">
          <label className="form-label">Meals</label>
          {includedItems.map((item, index) => (
            <div key={item.meal_id || index} className="row mt-2">
              <div className="col-md-4">
                <label className="form-label">Item</label>
                <input type="text" value={item.name} className="form-control" readOnly />
              </div>
              <div className="col-md-2">
                <label className="form-label">Quantity</label>
                <input type="text" value={item.qty} className="form-control" min="1" onChange={(event) => handleMealChange(index, "qty", Number(event.target.value))} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Note</label>
                <input type="text" value={item.note} className="form-control" onChange={(event) => handleMealChange(index, "note", event.target.value)} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {printQueue.length > 0 && (
        <div className="card border-primary mt-3">
          <div className="card-body">
            <div className="d-flex align-items-start justify-content-between gap-3">
              <div>
                <div className="text-muted small fw-bold text-uppercase">Antrian Print</div>
                <h4 className="mb-1">
                  {queueIndex + 1}/{printQueue.length} - {printQueue[queueIndex]?.label}
                </h4>
                <div className="text-muted small">
                  Klik Print untuk mencetak bagian ini, atau Skip untuk lanjut ke print berikutnya.
                </div>
              </div>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" onClick={handleSkipCurrent} disabled={isProcessing}>
                  Skip
                </Button>
                <Button variant="primary" onClick={handlePrintCurrent} disabled={isProcessing}>
                  {isProcessing ? "Printing..." : "Print Sekarang"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Row className="mt-3">
        <Col md={4}>
          <Button variant="primary" onClick={() => handleSaveAndPrint({ activity: true, meal: true })} disabled={isProcessing || printQueue.length > 0} block>
            {isProcessing ? "Processing..." : "Save & Print"}
          </Button>
        </Col>
        <Col md={4}>
          <Button variant="primary" onClick={() => handleSaveAndPrint({ activity: false, meal: true })} disabled={isProcessing || printQueue.length > 0} block>
            {isProcessing ? "Processing..." : "Save & Print Meal Only"}
          </Button>
        </Col>
        <Col md={4}>
          <Button variant="primary" onClick={() => handleSaveAndPrint({ activity: true, meal: false })} disabled={isProcessing || printQueue.length > 0} block>
            {isProcessing ? "Processing..." : "Save & Print Ticket Only"}
          </Button>
        </Col>
      </Row>
    </form>
  );
};

export default PrintTicket;
